import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Megaphone, Plus, Pin, PinOff, Trash2, Upload, X, Image as ImageIcon, User, Calendar, Building2, GripVertical } from "lucide-react";

const emptyForm = { title: "", body: "", image: "", pinned: false, unit_ids: [], all_units: true };

export default function StaffNotices() {
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);

  const load = async () => {
    try {
      const [n, u] = await Promise.all([api.get("/staff-notices"), api.get("/units")]);
      setItems(n.data || []);
      setUnits(u.data || []);
    } catch { toast.error("Erro ao carregar"); }
  };
  useEffect(() => { load(); }, []);

  const onImage = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 3 * 1024 * 1024) return toast.error("Imagem muito grande (máx 3MB)");
    const r = new FileReader();
    r.onload = () => setForm(fm => ({ ...fm, image: r.result }));
    r.readAsDataURL(f);
  };

  const toggleUnit = (uid) => {
    setForm(f => {
      const has = f.unit_ids.includes(uid);
      return { ...f, unit_ids: has ? f.unit_ids.filter(x => x !== uid) : [...f.unit_ids, uid] };
    });
  };

  const create = async () => {
    if (!form.title && !form.body && !form.image) return toast.error("Preencha título, texto ou imagem");
    if (!form.all_units && form.unit_ids.length === 0) return toast.error("Selecione ao menos uma unidade ou marque 'Todas'");
    setSaving(true);
    try {
      await api.post("/staff-notices", {
        title: form.title,
        body: form.body,
        image: form.image,
        pinned: form.pinned,
        unit_ids: form.all_units ? [] : form.unit_ids,
      });
      toast.success("Aviso publicado");
      setForm(emptyForm); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };

  const togglePin = async (n) => {
    try { await api.patch(`/staff-notices/${n.id}`, { pinned: !n.pinned }); load(); }
    catch { toast.error("Erro"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir aviso?")) return;
    try { await api.delete(`/staff-notices/${id}`); load(); toast.success("Removido"); }
    catch { toast.error("Erro"); }
  };

  const unitName = (uid) => units.find(u => u.id === uid)?.name || "?";

  const onDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", id); } catch {}
  };
  const onDragOver = (e, id) => {
    e.preventDefault();
    if (id !== overId) setOverId(id);
    e.dataTransfer.dropEffect = "move";
  };
  const onDragEnd = () => { setDragId(null); setOverId(null); };
  const onDrop = async (e, targetId) => {
    e.preventDefault();
    const sourceId = dragId;
    setDragId(null); setOverId(null);
    if (!sourceId || sourceId === targetId) return;
    const src = items.find(i => i.id === sourceId);
    const tgt = items.find(i => i.id === targetId);
    if (!src || !tgt) return;
    // Only allow reordering inside the same pinned/unpinned group
    if (!!src.pinned !== !!tgt.pinned) {
      toast.info("Só é possível reordenar dentro do mesmo grupo (fixados ou não-fixados)");
      return;
    }
    const filtered = items.filter(i => i.id !== sourceId);
    const idx = filtered.findIndex(i => i.id === targetId);
    filtered.splice(idx, 0, src);
    // Recompute order values per group
    let pOrder = 0, uOrder = 0;
    const withOrder = filtered.map(i => {
      const o = i.pinned ? pOrder++ : uOrder++;
      return { ...i, order: o };
    });
    setItems(withOrder);
    try {
      await api.put("/staff-notices/reorder", withOrder.map(i => ({ id: i.id, order: i.order })));
      toast.success("Ordem salva");
    } catch (err) { toast.error("Erro ao salvar ordem"); load(); }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Avisos (Usuários)"
        subtitle="Publique avisos internos pros usuários (recepção, personal, financeiro). Aparecem como tela inicial no login deles."
      />

      <Card className="p-5 border-slate-200" data-testid="staff-notice-form">
        <div className="flex items-center gap-2 mb-4">
          <Plus className="w-4 h-4 text-rose-600"/>
          <h3 className="font-heading font-bold text-slate-900">Novo aviso</h3>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <Label>Título</Label>
              <Input value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} placeholder="Ex: Fechamento antecipado dia 30" data-testid="notice-title"/>
            </div>
            <div>
              <Label>Texto</Label>
              <Textarea rows={5} value={form.body} onChange={(e)=>setForm({...form,body:e.target.value})} placeholder="Detalhes do aviso..." data-testid="notice-body"/>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.pinned} onChange={(e)=>setForm({...form,pinned:e.target.checked})} data-testid="notice-pinned"/>
              <span className="text-sm font-semibold text-slate-800 flex items-center gap-1"><Pin className="w-3.5 h-3.5 text-rose-600"/>Fixar no topo</span>
            </label>

            <div className="border-t border-slate-200 pt-3">
              <Label className="flex items-center gap-2 mb-2"><Building2 className="w-4 h-4 text-rose-600"/>Unidades que verão o aviso</Label>
              <label className="flex items-center gap-2 py-1.5 cursor-pointer" data-testid="notice-all-units-toggle">
                <Checkbox checked={form.all_units} onCheckedChange={(v)=>setForm({...form, all_units: !!v, unit_ids: v ? [] : form.unit_ids})}/>
                <span className="text-sm font-semibold text-slate-800">Publicar em todas as unidades</span>
              </label>
              {!form.all_units && (
                <div className="grid grid-cols-1 gap-1 max-h-32 overflow-y-auto pl-1 mt-1 border border-slate-200 rounded-lg p-2">
                  {units.map(u => (
                    <label key={u.id} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-slate-50 px-1 rounded" data-testid={`notice-unit-${u.id}`}>
                      <Checkbox checked={form.unit_ids.includes(u.id)} onCheckedChange={()=>toggleUnit(u.id)}/>
                      <span className="text-sm text-slate-700">{u.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div>
            <Label>Imagem (opcional)</Label>
            <div className="mt-1 border-2 border-dashed border-slate-300 rounded-xl p-4 text-center bg-slate-50 min-h-[180px] flex flex-col items-center justify-center">
              {form.image ? (
                <div className="relative">
                  <img src={form.image} alt="preview" className="max-h-40 rounded-lg" data-testid="notice-image-preview"/>
                  <Button size="icon" variant="destructive" onClick={()=>setForm({...form,image:""})} className="absolute -top-2 -right-2 w-6 h-6"><X className="w-3 h-3"/></Button>
                </div>
              ) : (
                <>
                  <ImageIcon className="w-10 h-10 text-slate-300 mb-2"/>
                  <label className="inline-flex items-center gap-2 cursor-pointer bg-slate-900 text-white px-3 py-2 rounded-lg text-sm font-bold uppercase hover:bg-black" data-testid="notice-image-upload">
                    <Upload className="w-4 h-4"/>Escolher imagem
                    <input type="file" accept="image/*" className="hidden" onChange={onImage}/>
                  </label>
                  <p className="text-xs text-slate-400 mt-2">Máx 3MB · JPG/PNG · Exibida no tamanho original</p>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={create} disabled={saving} className="bg-rose-600 hover:bg-rose-700 font-bold uppercase" data-testid="notice-publish">
            {saving ? "Publicando..." : (<><Megaphone className="w-4 h-4 mr-2"/>Publicar</>)}
          </Button>
        </div>
      </Card>

      <div className="space-y-3" data-testid="staff-notices-list">
        {items.map(n => {
          const allUnits = !n.unit_ids || n.unit_ids.length === 0;
          const isDragOver = overId === n.id && dragId !== n.id;
          const isDragging = dragId === n.id;
          return (
            <Card
              key={n.id}
              draggable
              onDragStart={(e)=>onDragStart(e, n.id)}
              onDragOver={(e)=>onDragOver(e, n.id)}
              onDragEnd={onDragEnd}
              onDrop={(e)=>onDrop(e, n.id)}
              className={`p-4 border-l-4 transition-all ${n.pinned ? "border-l-rose-500 bg-rose-50/40" : "border-l-slate-200"} ${isDragging ? "opacity-40" : ""} ${isDragOver ? "ring-2 ring-rose-400 ring-offset-1 -translate-y-0.5" : ""}`}
              data-testid={`staff-notice-${n.id}`}>
              <div className="flex items-start gap-3">
                <div className="flex flex-col items-center justify-center shrink-0 pt-1 cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-700" title="Arraste para reordenar" data-testid={`notice-drag-${n.id}`}>
                  <GripVertical className="w-5 h-5"/>
                </div>
                {n.image && <img src={n.image} alt="" className="w-24 max-h-24 object-contain rounded-lg shrink-0 bg-slate-100"/>}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {n.pinned && <span className="text-[10px] px-2 py-0.5 rounded bg-rose-500 text-white font-bold uppercase tracking-widest">📌 Fixado</span>}
                    {n.title && <h4 className="font-heading text-lg font-bold text-slate-900">{n.title}</h4>}
                  </div>
                  {n.body && <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{n.body}</p>}
                  <div className="mt-2 flex items-start gap-1.5 flex-wrap">
                    <Building2 className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0"/>
                    {allUnits ? (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px] font-bold uppercase">Todas as unidades</Badge>
                    ) : (
                      n.unit_ids.map(uid => (
                        <Badge key={uid} className="bg-rose-100 text-rose-700 hover:bg-rose-100 text-[10px] font-semibold">{unitName(uid)}</Badge>
                      ))
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-400 mt-2">
                    <span className="flex items-center gap-1"><User className="w-3 h-3"/>{n.created_by_name || "—"}</span>
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3"/>{new Date(n.created_at).toLocaleString("pt-BR")}</span>
                  </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <Button variant="ghost" size="icon" onClick={()=>togglePin(n)} title={n.pinned ? "Desafixar" : "Fixar"} data-testid={`notice-toggle-pin-${n.id}`}>
                    {n.pinned ? <PinOff className="w-4 h-4 text-slate-500"/> : <Pin className="w-4 h-4 text-rose-500"/>}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={()=>remove(n.id)} title="Excluir" data-testid={`notice-delete-${n.id}`}><Trash2 className="w-4 h-4 text-rose-400"/></Button>
                </div>
              </div>
            </Card>
          );
        })}
        {items.length === 0 && (
          <div className="p-10 text-center text-slate-400 border-2 border-dashed border-slate-200 rounded-xl">
            <Megaphone className="w-10 h-10 text-slate-300 mx-auto mb-2"/>
            Nenhum aviso publicado. Crie o primeiro acima.
          </div>
        )}
      </div>
    </div>
  );
}
